import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  useStyledTheme,
  WarningIcon,
} from '@equifinance/shared';
import { useMemo } from 'react';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import { Application, UnderwritingStatuses } from '../../../contexts/applications';

const DateReceivedWrapper = styled(FlexColumn)<{ type: 'amber' | 'red' | 'green' | 'default'}>`${({ theme, type }) => `
  padding: ${theme.padding[3]};
  gap: ${theme.padding[1]};
  align-items: center;
  border-radius: ${theme.padding[3]};
  > i {
    display: flex;
  }
  ${type === 'green' && `{
    background-color: ${theme.colours.success[3]};
  }`};
  ${type === 'amber' && `{
    background-color: ${theme.colours.warning[2]};
  }`};
  ${type === 'red' && `{
    background-color: ${theme.colours.danger[3]};
  }`};
`}`;

const IconContainer = styled(Flex)`${({ theme }) => `
  width: ${theme.padding[4]};
`}`;

const DateReceivedColumnContainer = styled(Flex)`
  width: 100%;
  max-width: 100%;
  align-items: center;
`;

const DatetimeText = styled.span`${({ theme }) => `
  flex-grow: 1;
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
`}`;

export default function DateReceivedColumn({ row }: { row: Application }) {
  const theme = useStyledTheme();
  const currentTime = useMemo(() => new Date().getTime(), []);
  const alert: 'amber' | 'red' | 'green' | 'default' = useMemo(() => {
    if (row.underwritingStatus !== UnderwritingStatuses.Underwriting) {
      return 'default';
    }
    if (row.slaAmberTrigger && currentTime >= new Date(row.slaAmberTrigger).getTime()) {
      return 'amber';
    }
    if (row.slaRedTrigger && currentTime >= new Date(row.slaRedTrigger).getTime()) {
      return 'red';
    }
    if (row.slaAmberTrigger && currentTime < new Date(row.slaAmberTrigger).getTime()) {
      return 'green';
    }
    return 'default';
  }, [row.slaAmberTrigger, row.slaRedTrigger]);

  const formattedSubmitionDate = useMemo(() => formatDateTime(row.submissionDate, DateFormatStringEnum.DDMMYYYY), [row.submissionDate]);

  return (
    <DateReceivedColumnContainer>
      <DatetimeText>
        <DateReceivedWrapper type={alert}>
          {['amber', 'red'].includes(alert) && (
            <IconContainer>
              <WarningIcon
                stroke={alert === 'amber' ? theme.colours.warning[1] : theme.colours.danger[1]}
                fill={theme.colours.neutral[10]}
              />
            </IconContainer>
          )}
          {row.submissionDate ? formattedSubmitionDate : '-'}
        </DateReceivedWrapper>
      </DatetimeText>
    </DateReceivedColumnContainer>
  );
}
